// Utility for computing event durations and aggregating hours across views
// Single source of truth for duration math used by Hours/Admin pages.

import { parseISO, differenceInMinutes, isValid } from "date-fns";

export const DEFAULT_DURATION_MINUTES = 60;

// Attempt to parse various date representations safely
const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === "number") {
    const d = new Date(value);
    return isValid(d) ? d : null;
  }
  if (typeof value === "string") {
    const d = parseISO(value);
    return isValid(d) ? d : null;
  }
  return null;
};

// Extract likely start/end keys from an event object
const getStartEnd = (event) => {
  if (!event || typeof event !== "object") return { start: null, end: null };

  const startRaw =
    event.start ??
    event.startTime ??
    event.start_time ??
    event.begin ??
    event.beginTime;
  const endRaw =
    event.end ??
    event.endTime ??
    event.end_time ??
    event.finish ??
    event.finishTime;

  const start = parseDate(startRaw);
  const end = parseDate(endRaw);

  return { start, end };
};

// Derive a reasonable classType key for defaults lookup
const getClassType = (event) => {
  if (!event || typeof event !== "object") return undefined;
  return (
    event.classType ??
    event.class_type ??
    event.class ??
    event.type ??
    event.category
  );
};

/**
 * Returns the event duration in minutes.
 * Priority:
 *  1) event.duration (assumed minutes)
 *  2) Derived from start/end or startTime/endTime (DST-safe via date-fns)
 *  3) defaults[classType] if available
 *  4) fallbackMinutes (default 60)
 *
 * @param {Object} event
 * @param {Record<string, number>} defaults - map of classType -> default minutes
 * @param {number} fallbackMinutes - default if nothing else available
 * @returns {number} minutes
 */
export function getEventDurationMinutes(
  event,
  defaults = {},
  fallbackMinutes = DEFAULT_DURATION_MINUTES
) {
  if (!event) return 0;

  // 1) explicit duration on event
  const explicit = Number(event.duration);
  if (Number.isFinite(explicit) && explicit > 0) {
    return explicit;
  }

  // 2) derive from start/end variants
  const { start, end } = getStartEnd(event);
  if (start && end) {
    const mins = differenceInMinutes(end, start);
    // Guard against negative (bad data)
    if (Number.isFinite(mins) && mins > 0) return mins;
  }

  // 3) default by class type
  const classType = getClassType(event);
  const byType = Number(defaults?.[classType]);
  if (Number.isFinite(byType) && byType > 0) {
    return byType;
  }

  // 4) global fallback
  return fallbackMinutes;
}

/**
 * Sum an array of events into total hours (float).
 * @param {Array<Object>} events
 * @param {Record<string, number>} defaults
 * @param {number} fallbackMinutes
 * @returns {number} hours
 */
export function sumEventsHours(
  events = [],
  defaults = {},
  fallbackMinutes = DEFAULT_DURATION_MINUTES
) {
  if (!Array.isArray(events)) return 0;
  const totalMinutes = events.reduce((sum, ev) => {
    return sum + getEventDurationMinutes(ev, defaults, fallbackMinutes);
  }, 0);
  return totalMinutes / 60;
}

/**
 * Sum a map of coachName -> events[] into coachName -> hours.
 * @param {Record<string, Array<Object>>} eventsByCoach
 * @param {Record<string, number>} defaults
 * @param {number} fallbackMinutes
 * @returns {Record<string, number>} coachHours
 */
export function sumHoursByCoach(
  eventsByCoach = {},
  defaults = {},
  fallbackMinutes = DEFAULT_DURATION_MINUTES
) {
  const output = {};
  if (!eventsByCoach || typeof eventsByCoach !== "object") return output;

  Object.entries(eventsByCoach).forEach(([coachName, events]) => {
    output[coachName] = sumEventsHours(events, defaults, fallbackMinutes);
  });

  return output;
}

/**
 * Count events across array or dict-of-arrays.
 * @param {Array|Object} data
 * @returns {number}
 */
export function countEvents(data) {
  if (Array.isArray(data)) return data.length;
  if (data && typeof data === "object") {
    return Object.values(data).reduce((acc, arr) => {
      return acc + (Array.isArray(arr) ? arr.length : 0);
    }, 0);
  }
  return 0;
}
